/**
 * proofUrlResolver — admin-side helper for GitHub issue #22.
 * Exchanges a proof storage reference for a short-lived signed URL minted by
 * `get_signed_proof_url` in api/admin/payment-operations.js.
 *
 * Wave 4: fail-closed. Signable proof refs return a signed URL or null.
 * They must NOT fall back to a public `/object/public/proof/` URL — that
 * path 403s once the bucket is private. Non-proof http(s) URLs (explorers)
 * still pass through unchanged.
 */

import { getServerSessionId } from './serverSession'

const PUBLIC_PROOF_MARKER = '/object/public/proof/'
const SIGNED_PROOF_MARKER = '/object/sign/proof/'

/**
 * True only for values that are (or look like) a proof storage reference the
 * signer can resolve: a public-proof URL, a signed-proof URL, or a non-URL
 * bare storage key. Explicitly false for other http(s) URLs (wallet explorers,
 * external links).
 */
export function isSignableProofRef(raw: string | null | undefined): boolean {
  if (!raw || typeof raw !== 'string') return false
  const v = raw.trim()
  if (!v) return false
  if (v.includes(PUBLIC_PROOF_MARKER) || v.includes(SIGNED_PROOF_MARKER)) return true
  // Any other absolute URL is NOT a proof object we can sign — skip it.
  if (/^https?:\/\//i.test(v)) return false
  // Non-URL string: treat as a bare storage key (optionally 'proof/'-prefixed
  // or leading-slash), but reject path traversal outright.
  if (v.includes('..')) return false
  return true
}

/**
 * Exchange a raw proof reference for a short-lived signed URL via the admin
 * endpoint. Returns the signed URL on success. For signable proof refs,
 * returns null on any signer failure (never the original public URL).
 * Non-signable http(s) URLs are returned unchanged. `expiresIn` is clamped
 * server-side to 60..3600s.
 */
export async function resolveProofUrl(
  rawUrl: string | null | undefined,
  expiresIn = 900
): Promise<string | null> {
  if (!rawUrl || typeof rawUrl !== 'string') return null
  const original = rawUrl.trim()
  if (!original) return null
  // Not a proof object we should sign (wallet/explorer/external) — pass through.
  if (!isSignableProofRef(original)) return original

  try {
    const sessionId = getServerSessionId()
    const response = await fetch('/api/admin/payment-operations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {})
      },
      body: JSON.stringify({
        operation: 'get_signed_proof_url',
        data: { path: original, expiresIn }
      })
    })

    if (!response.ok) return null
    const result = await response.json()
    const signed = result?.data?.signedUrl
    return typeof signed === 'string' && signed.length > 0 ? signed : null
  } catch {
    return null
  }
}
